import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Users } from "lucide-react";
import { useSchedule, generateTimeSlots } from "@/lib/schedule";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GroupOrderModal = ({ isOpen, onClose, language }) => {
  const [formData, setFormData] = useState({
    creator_name: "",
    creator_phone: "",
    creator_email: "",
    delivery_type: "delivery", // "delivery" or "pickup"
    delivery_address: "",
    delivery_time: "",
    notes: ""
  });
  const [loading, setLoading] = useState(false);
  const [groupOrderCreated, setGroupOrderCreated] = useState(false);
  const [groupOrderId, setGroupOrderId] = useState("");
  const [addressValidation, setAddressValidation] = useState(null);
  const [validatingAddress, setValidatingAddress] = useState(false);
  const schedule = useSchedule(isOpen);
  const [restaurantOpen, setRestaurantOpen] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    axios.get(`${API}/restaurant-status`).then((res) => setRestaurantOpen(res.data.is_open !== false)).catch(() => {});
  }, [isOpen]);

  const translations = {
    title: {
      en: "Create Group Order",
      ru: "Создать групповой заказ",
      lv: "Izveidot grupas pasūtījumu"
    },
    subtitle: {
      en: "Share the link with friends so everyone can add their items",
      ru: "Поделитесь ссылкой с друзьями, чтобы каждый мог добавить свои блюда",
      lv: "Dalieties ar saiti ar draugiem, lai katrs varētu pievienot savus ēdienus"
    },
    name: {
      en: "Your Name",
      ru: "Ваше имя",
      lv: "Jūsu vārds"
    },
    phone: {
      en: "Your Phone",
      ru: "Ваш телефон",
      lv: "Jūsu tālrunis"
    },
    email: {
      en: "Your Email (Optional)",
      ru: "Ваш Email (необязательно)",
      lv: "Jūsu e-pasts (nav obligāti)"
    },
    address: {
      en: "Delivery Address",
      ru: "Адрес доставки",
      lv: "Piegādes adrese"
    },
    deliveryTime: {
      en: "Delivery Time (Optional)",
      ru: "Время доставки (необязательно)",
      lv: "Piegādes laiks (nav obligāti)"
    },
    asap: {
      en: "ASAP (30 min - 1 hour)",
      ru: "Как можно скорее (30 мин - 1 час)",
      lv: "ASAP (30 min - 1 stunda)"
    },
    notes: {
      en: "Notes (Optional)",
      ru: "Примечания (необязательно)",
      lv: "Piezīmes (nav obligāti)"
    },
    deliveryType: {
      en: "Order Type",
      ru: "Тип заказа", 
      lv: "Pasūtījuma veids"
    },
    delivery: {
      en: "Delivery",
      ru: "Доставка",
      lv: "Piegāde"
    },
    pickup: {
      en: "Pickup",
      ru: "Самовывоз",
      lv: "Paņemšana"
    },
    pickupAddress: {
      en: "Pickup at: Liepu iela 9, Liepāja, LV-3401",
      ru: "Pickup at: Liepu iela 9, Liepāja, LV-3401",
      lv: "Saņemt: Liepu iela 9, Liepāja, LV-3401"
    },
    create: {
      en: "Create Group Order",
      ru: "Создать групповой заказ",
      lv: "Izveidot grupas pasūtījumu"
    },
    cancel: {
      en: "Cancel",
      ru: "Отмена",
      lv: "Atcelt"
    },
    success: {
      en: "Group order created! Share this link:",
      ru: "Групповой заказ создан! Поделитесь этой ссылкой:",
      lv: "Grupas pasūtījums izveidots! Dalieties ar šo saiti:"
    },
    copyLink: {
      en: "Copy Link",
      ru: "Копировать ссылку",
      lv: "Kopēt saiti"
    },
    linkCopied: {
      en: "Link copied to clipboard!",
      ru: "Ссылка скопирована в буфер обмена!",
      lv: "Saite nokopēta starpliktuvē!"
    },
    viewOrder: {
      en: "View Group Order",
      ru: "Посмотреть групповой заказ",
      lv: "Skatīt grupas pasūtījumu"
    }
  };

  const timeSlots = generateTimeSlots(schedule, language, restaurantOpen ? translations.asap[language] : null);

  useEffect(() => {
    if (isOpen && !restaurantOpen && !formData.delivery_time && timeSlots.length > 0) {
      setFormData(prev => ({ ...prev, delivery_time: timeSlots[0].value }));
    }
  }, [isOpen, restaurantOpen, timeSlots.length]);

  const validateAddress = async () => {
    if (!formData.delivery_address) {
      return;
    }
    
    setValidatingAddress(true);
    try {
      const response = await axios.post(`${API}/validate-address`, {
        address: formData.delivery_address
      });
      setAddressValidation(response.data);
    } catch (error) {
      console.error("Address validation error:", error);
      setAddressValidation(null);
    } finally {
      setValidatingAddress(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.creator_name || !formData.creator_phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate delivery address only if delivery type is "delivery"
    if (formData.delivery_type === "delivery") {
      if (!formData.delivery_address) {
        toast.error("Please fill in all required fields");
        return;
      }

      // Validate address if not done yet
      if (!addressValidation) {
        await validateAddress();
      }

      if (addressValidation && !addressValidation.valid) {
        toast.error(addressValidation.message);
        return;
      }
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/group-orders`, formData);
      setGroupOrderId(response.data.id);
      setGroupOrderCreated(true);
      toast.success(translations.success[language]);
    } catch (error) {
      console.error("Error creating group order:", error);
      toast.error("Failed to create group order. Please try again.");
      setLoading(false);
    }
  };

  const copyLink = async () => {
    // URL encode the group order ID to handle the # symbol
    const encodedGroupOrderId = encodeURIComponent(groupOrderId);
    const groupOrderUrl = `${window.location.origin}/group-order/${encodedGroupOrderId}`;
    
    try {
      // Try modern Clipboard API first
      await navigator.clipboard.writeText(groupOrderUrl);
      toast.success(translations.linkCopied[language]);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API or have it blocked
      try {
        // Create a temporary textarea
        const textArea = document.createElement('textarea');
        textArea.value = groupOrderUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        // Try to copy using execCommand (older method)
        const successful = document.execCommand('copy');
        textArea.remove();
        
        if (successful) {
          toast.success(translations.linkCopied[language]);
        } else {
          // If both methods fail, show the link for manual copying
          toast.error('Could not copy automatically. Please copy manually: ' + groupOrderUrl);
        }
      } catch (fallbackErr) {
        console.error('Copy failed:', fallbackErr);
        toast.error('Copy failed. Link: ' + groupOrderUrl);
      }
    }
  };

  const viewGroupOrder = () => {
    // URL encode the group order ID to handle the # symbol
    const encodedGroupOrderId = encodeURIComponent(groupOrderId);
    window.open(`/group-order/${encodedGroupOrderId}`, '_blank');
  };

  if (groupOrderCreated) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="rz-form max-w-2xl bg-[#FDFBF7] border border-[#E7E5E4] rounded-none p-7 sm:p-10">
          <DialogHeader>
            <DialogTitle className="font-display text-[2rem] leading-none text-[#1C1917] flex items-center gap-3">
              <Users className="w-6 h-6 text-green-600" />
              {translations.success[language]}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rz-note rz-note--ok">
              <p className="text-green-800 font-medium mb-2">Group Order Link:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-2 bg-white border rounded text-sm">
                  {`${window.location.origin}/group-order/${encodeURIComponent(groupOrderId)}`}
                </code>
                <Button onClick={copyLink} size="sm" variant="outline">
                  <Copy className="w-4 h-4 mr-1" />
                  {translations.copyLink[language]}
                </Button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={viewGroupOrder} className="rz-btn flex-1 min-w-0" data-testid="group-order-view-button">
                <Users className="w-4 h-4" strokeWidth={1.5} />
                {translations.viewOrder[language]}
              </button>
              <button onClick={onClose} className="rz-btn rz-btn--outline flex-1 min-w-0" data-testid="group-order-close-button">
                {translations.cancel[language]}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="rz-form max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FDFBF7] border border-[#E7E5E4] rounded-none p-7 sm:p-10">
        <DialogHeader>
          <DialogTitle className="font-display text-[2rem] leading-none text-[#1C1917] flex items-center gap-3">
            <Users className="w-6 h-6 text-[#1C1917]" />
            {translations.title[language]}
          </DialogTitle>
          <p className="text-[#78716C]">{translations.subtitle[language]}</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="space-y-2">
            <Label htmlFor="creator_name">{translations.name[language]} *</Label>
            <Input
              id="creator_name"
              value={formData.creator_name}
              onChange={(e) => setFormData({ ...formData, creator_name: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="creator_phone">{translations.phone[language]} *</Label>
            <Input
              id="creator_phone"
              value={formData.creator_phone}
              onChange={(e) => setFormData({ ...formData, creator_phone: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="creator_email">{translations.email[language]}</Label>
            <Input
              id="creator_email"
              type="email"
              value={formData.creator_email}
              onChange={(e) => setFormData({ ...formData, creator_email: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>{translations.deliveryType[language]}</Label>
            <RadioGroup 
              value={formData.delivery_type} 
              onValueChange={(value) => setFormData({ ...formData, delivery_type: value, delivery_address: value === "pickup" ? "" : formData.delivery_address })}
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="delivery" id="group-delivery" />
                <Label htmlFor="group-delivery" className="font-normal">{translations.delivery[language]}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="pickup" id="group-pickup" />
                <Label htmlFor="group-pickup" className="font-normal">{translations.pickup[language]}</Label>
              </div>
            </RadioGroup>
          </div>

          {formData.delivery_type === "pickup" ? (
            <div className="rz-note">
              <p className="font-medium text-[#1C1917] m-0">{translations.pickupAddress[language]}</p>
              <p className="m-0 mt-1">+371 28717331</p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="delivery_address">{translations.address[language]} *</Label>
              <AddressAutocomplete
              value={formData.delivery_address}
              onChange={(value) => {
                setFormData({ ...formData, delivery_address: value });
                setAddressValidation(null);
              }}
              onBlur={validateAddress}
              onValidation={(address) => {
                setFormData({ ...formData, delivery_address: address });
                validateAddress();
              }}
              language={language}
            />
            {validatingAddress && (
              <div className="flex items-center gap-2 text-sm text-[#78716C]">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>Checking delivery area...</span>
              </div>
            )}
            {addressValidation && (
              <div className={`rz-note ${addressValidation.valid ? "rz-note--ok" : "rz-note--warn"}`}>
                {addressValidation.valid ? '✅' : '❌'} {addressValidation.message}
              </div>
            )}
          </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="delivery_time">{translations.deliveryTime[language]}</Label>
            <select
              id="delivery_time"
              data-testid="group-delivery-time-select"
              value={formData.delivery_time}
              onChange={(e) => setFormData({ ...formData, delivery_time: e.target.value })}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {timeSlots.map((slot, index) => (
                <option key={index} value={slot.value}>
                  {slot.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">{translations.notes[language]}</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
            <button type="button" onClick={onClose} className="rz-btn rz-btn--outline flex-1 min-w-0" data-testid="group-order-cancel-button">
              {translations.cancel[language]}
            </button>
            <button type="submit" disabled={loading} className="rz-btn flex-1 min-w-0" data-testid="group-order-create-button">
              {loading ? "..." : translations.create[language]}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default GroupOrderModal;